import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Box, Button, Typography, TextField, Checkbox, FormControlLabel } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import "./authorizedUsers.scss";

import {ReactComponent as AccountCircleIcon} from "../../../assets/images/icons/account_circle.svg"
import SectionHeader from "../../../components/SectionHeader/sectionHeader";
import TransitionAlerts from "../../../components/Toasts/Index.transitionAlert";

import { useAppDispatch, useAppSelector } from "../../../redux/store/store";
import { authorizedUserAction } from "../../../redux/authorizedUsers/authorizedUsers.actions";
import { getCustomerUsersData } from "../../../redux/authorizedUsers/authorizedUsers.selectors";
import { userObject } from "../../../redux/auth/auth.selectors";
import RemoveUser from "./Modals/RemoveUserModal";
import { commonUtils } from "../../../Utils/utils";


type UserDetails = {
    id: string;
    // add other properties if needed
  };
export default function AuthorizedUsers() {
   
  // Selectors
  const userDetails = useAppSelector(userObject) as UserDetails;
  const CustomerUserDetials = useAppSelector(getCustomerUsersData);  
    
  // States
  const [showForm, setShowForm] = useState(false);
  const [removeUserModal, setRemoveUserModal] = useState(false);
  const [alert, setAlert] = useState({ 
    message: "",
    type: "success", // can be success, error, info, warning
    show: false,
  });

  
  // Hooks
  const dispatch = useAppDispatch();

  // Effect To fetching The Client User Details
  useEffect(() =>{
    let payload = {
      Id : userDetails.id,
    }
    dispatch(authorizedUserAction.getCustomerUsersAction(payload))
  },[dispatch, userDetails.id])

  //Effect to alert disappears after a few seconds
  useEffect(() => {
  if (alert.show) {
    const timer = setTimeout(() => {
      setAlert(prev => ({ ...prev, show: false }));
    }, 4000);
    return () => clearTimeout(timer);
  }
}, [alert.show]);


 // Formik Initial Values
  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    isReadOnly: true, // New field for read-only users
  };

 // Formik Validation
const validationSchema = Yup.object().shape({
  firstName: Yup.string()
    .matches(/^[A-Za-z]+$/, "First name must contain only letters")
    .required("Required"),
  lastName: Yup.string()
    .matches(/^[A-Za-z]+$/, "Last name must contain only letters")
    .required("Required"),
  email: Yup.string()
    .email("Invalid email")
    .required("Required"),
  phone: Yup.string()
    .required("Required")
    .test(
      "is-10-digits",
      "Phone must be exactly 10 digits",
      value => {
        if (!value) return true; // optional
        const digits = value.replace(/\D/g, "");
        return digits.length === 10;
      }
    ),
  isReadOnly: Yup.boolean(), // Validation for the new checkbox
});

  // Handle Add User Function
  const handleAddUser = (values: typeof initialValues) => {
    // Payload For The API Call
    const payload = {
      body: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        // Phone is Optional
        ...(values.phone ? { phone: commonUtils.purgeAndFormatPhoneNumber(values.phone) } : {}),
        // Set user role based on isReadOnly checkbox
        isReadOnly: values.isReadOnly, // If read-only is checked, user is NOT primary
      },
    };

    // API Call For Create Customer User
    dispatch(authorizedUserAction.createCustomerUserAction(payload))
      .then((res: any) => {

        // Extract relevant status and message from response
        const status = res?.payload?.data?.status;
        const message = res?.payload?.data?.message;

        if (status === 200) {
          //Get Updated Detials
          dispatch(authorizedUserAction.getCustomerUsersAction(payload));
          setAlert({
            message: message || "User created successfully.",
            type: "success",
            show: true,
          });
        } else {
          setAlert({
            message: message || "Failed to create user.",
            type: "error",
            show: true,
          });
        }
      })
      .catch((err: any) => {
        console.log("err", err);
        setAlert({
          message: err?.message || "Failed to create user.",
          type: "error",
          show: true,
        });
      });
    }
  

  return (
    <>
    <Box className="authorized-users-container">
      <SectionHeader className="boxTitle section-heading">
        <h4>Authorized Users</h4>
      </SectionHeader>

      <Box className="authorized-users-warning">
        <div className="warn-container">
          <p className="ac-pref-warn-text">
            <WarningAmberIcon /> Note: Full access users can see all information and perform any changes or actions. <br />
            Read-only users can view information but cannot make changes or perform restricted actions.
          </p>
        </div>
      </Box>

      {!showForm && (
        <Button
          className="add-new-user-button"
          onClick={() => setShowForm(true)}
        >
          Add New User
        </Button>
      )}

      {showForm && (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { resetForm }) => {
                console.log("Submitting values:", values);

            handleAddUser(values);
            resetForm();
            setShowForm(false);
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleReset, setFieldValue }) => (
            <Form className="add-user-form">
              {/* Row 1: First Name and Last Name */}
              <div className="input-row">
                <Box className="input-group">
                  <label className="static-label">
                    First Name<span className="required">*</span>
                  </label>
                  <TextField
                    fullWidth
                    placeholder="First Name"
                    name="firstName"
                    variant="outlined"
                    size="small"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.firstName && Boolean(errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                  />
                </Box>
                <Box className="input-group">
                  <label className="static-label">
                    Last Name<span className="required">*</span>
                  </label>
                  <TextField
                    fullWidth
                    placeholder="Last Name"
                    name="lastName"
                    variant="outlined"
                    size="small"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.lastName && Boolean(errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                  />
                </Box>
              </div>

              {/* Row 2: Email Address and Phone Number */}
              <div className="input-row">
                <Box className="input-group">
                  <label className="static-label">
                    Email Address<span className="required">*</span>
                  </label>
                  <TextField
                    fullWidth
                    placeholder="Email Address"
                    name="email"
                    type="email"
                    variant="outlined"
                    size="small"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Box>
                <Box className="input-group">
                  <label className="static-label">
                    Phone Number<span className="required">*</span>
                  </label>
                  <TextField
                    fullWidth
                    placeholder="Phone Number"
                    name="phone"
                    variant="outlined"
                    size="small"
                    value={values.phone}
                    onChange={e => {
                      // Only allow digits, max 10, and format as (999) 999 9999
                      let raw = e.target.value.replace(/\D/g, "").slice(0, 10);
                      let formatted = commonUtils.formatPhoneNumber(raw);
                      setFieldValue("phone", formatted);
                    }}
                    onBlur={handleBlur}
                    inputProps={{ maxLength: 14 }}
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                  />
                </Box>
              </div>

              {/* Row 3: Read Only Checkbox and Action Buttons */}
              <div className="input-row checkbox-actions-row">
                <Box className="input-group checkbox-group">
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isReadOnly"
                        checked={values.isReadOnly}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label={
                      <span className="checkbox-label">
                        Read Only
                        <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.5 }}>
                          Read-only users can view information but cannot make changes or perform actions
                        </Typography>
                      </span>
                    }
                  />
                </Box>
                
                {/* Action Buttons */}
                <Box className="actions-inline">
                  <Button
                    className="cancel-button"
                    variant="text"
                    onClick={() => {
                      handleReset();
                      setShowForm(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="add-user-button"
                  >
                    Add User
                  </Button>
                </Box>
              </div>
            </Form>
          )}
        </Formik>
      )}

      <Box className="users-list">
        {
          CustomerUserDetials.data?.map((user: any, index : number) =>(
          <Box key={index} className="user-item">
            <Box className="user-info">
              <AccountCircleIcon className="user-icon" />
              <Box className="user-details">
                <Typography className="user-name" variant="body1" fontWeight={700}>
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography className="user-email" variant="body2" color="text.secondary" title={user.email}>
                  {user.email}
                </Typography>
                <Typography className="user-phone" variant="body2" color="text.secondary">
                  {commonUtils.formatPhoneNumber(user.phone)}
                </Typography>
              </Box>
            </Box>
            <Button
              className="remove-user-button"
              variant="text"
              onClick={() => setRemoveUserModal(user.id)}
            >
              Remove
            </Button>
          </Box>
          ))
        }
      </Box>
    </Box>

      {/* Showing Toasts Alert  */}
      {
         alert && (
              <TransitionAlerts
                errorData={alert.message}
                showAlert={alert.show}
                type={alert.type}
                action={true}
              />
         )
      }
      {/* Remove User Modal */}
      {
        removeUserModal && (
          <RemoveUser
            open={true}
            setAlert={setAlert}
            id={removeUserModal}
            showAlert={alert.show}
            handleModalClose={() => setRemoveUserModal(false)}
          />
        )
      }
    </>

  );
}
